<script setup>
import { ref, reactive, onMounted } from 'vue';
import Icon from '../components/IconComponent.vue';
import { api } from '../services/api';

defineProps({
    navigate: {
        type: Function,
        required: true
    }
});

const plans = ref([]);
const isLoadingPlans = ref(false);
const plansError = ref('');

const selectedPlan = ref(null);
const showForm = ref(false);
const isSubmitting = ref(false);
const formError = ref('');
const form = reactive({ full_name: '', email: '' });

const formatPrice = (kobo, currency) => {
    const amount = (Number(kobo) || 0) / 100;
    return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: currency || 'NGN',
        maximumFractionDigits: 0
    }).format(amount);
};

async function loadPlans() {
    isLoadingPlans.value = true;
    plansError.value = '';
    try {
        plans.value = await api.getPlans();
    } catch (error) {
        plansError.value = error.message || 'Unable to load consultation plans.';
    } finally {
        isLoadingPlans.value = false;
    }
}

function openForm(plan) {
    selectedPlan.value = plan;
    form.full_name = '';
    form.email = '';
    formError.value = '';
    showForm.value = true;
}

function closeForm() {
    if (isSubmitting.value) return;
    showForm.value = false;
}

async function submitBooking() {
    formError.value = '';
    if (form.full_name.trim().length < 2) {
        formError.value = 'Please enter your full name.';
        return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
        formError.value = 'Please enter a valid email address.';
        return;
    }

    isSubmitting.value = true;
    try {
        const result = await api.initializePayment({
            plan_code: selectedPlan.value.code,
            full_name: form.full_name.trim(),
            email: form.email.trim()
        });
        if (!result || !result.authorization_url) {
            throw new Error('Payment initialization failed. Please try again.');
        }
        window.location.href = result.authorization_url;
    } catch (error) {
        formError.value = error.message || 'We could not start the payment. Please try again.';
        isSubmitting.value = false;
    }
}

onMounted(loadPlans);
</script>

<template>
    <main class="container mx-auto px-4 py-16">
        <div class="text-center mb-12" v-reveal:up>
            <h1 class="text-4xl md:text-5xl font-extrabold text-gray-800 mb-3">Consult us Today.</h1>
            <p class="text-gray-600 max-w-2xl mx-auto">Take the first step towards your study abroad journey. Our expert advisors are here to help you and your family navigate the process.</p>
        </div>

        <!-- Loading -->
        <div v-if="isLoadingPlans" class="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div v-for="n in 2" :key="n" class="bg-white p-8 md:p-10 shadow-xl rounded-3xl border-2 border-transparent animate-pulse">
                <div class="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
                <div class="h-10 bg-gray-200 rounded w-1/3 mb-6"></div>
                <div class="h-4 bg-gray-200 rounded w-full mb-2"></div>
                <div class="h-4 bg-gray-200 rounded w-5/6 mb-8"></div>
                <div class="h-12 bg-gray-200 rounded-xl w-full"></div>
            </div>
        </div>

        <!-- Error -->
        <div v-else-if="plansError" class="max-w-xl mx-auto text-center bg-white border border-rose-100 rounded-2xl p-8 shadow-sm">
            <Icon name="alert-circle" class-name="w-8 h-8 text-primary mx-auto mb-3" />
            <p class="text-gray-700 mb-5">{{ plansError }}</p>
            <button @click="loadPlans" class="inline-flex items-center px-6 py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition duration-300">
                Try Again
            </button>
        </div>

        <!-- Plan cards (from the database) -->
        <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div v-for="(plan, index) in plans" :key="plan.code"
                 v-reveal:up="index * 150"
                 :class="[
                     'bg-white p-8 md:p-10 shadow-xl rounded-3xl border-2 flex flex-col transition duration-300 hover:scale-[1.02]',
                     plan.highlighted ? 'border-primary' : 'border-transparent'
                 ]">
                <div class="mb-8">
                    <h2 class="text-2xl font-bold text-gray-800 mb-4">{{ plan.name }}</h2>
                    <div class="flex items-baseline mb-4">
                        <span class="text-4xl font-extrabold text-gray-900">{{ formatPrice(plan.price_kobo, plan.currency) }}</span>
                        <span class="text-gray-500 ml-2">/ session</span>
                    </div>
                    <p class="text-gray-600 leading-relaxed">{{ plan.description }}</p>
                </div>

                <ul class="space-y-4 mb-10 flex-grow">
                    <li v-for="feature in (plan.features || [])" :key="feature" class="flex items-center text-gray-700">
                        <div class="bg-primary/10 p-1 rounded-full mr-3">
                            <Icon name="checkmark-circle" class-name="w-4 h-4 text-primary" />
                        </div>
                        <span>{{ feature }}</span>
                    </li>
                </ul>

                <button
                    @click="openForm(plan)"
                    class="w-full py-4 rounded-xl font-bold transition duration-300 shadow-lg text-center"
                    :class="[
                        plan.highlighted
                        ? 'bg-primary text-white hover:bg-primary/90 shadow-primary/20'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    ]"
                >
                    {{ plan.button_text || 'Book Session' }}
                </button>
            </div>
        </div>

        <!-- Booking / payment form -->
        <div v-if="showForm && selectedPlan" class="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-black/50" @click.self="closeForm">
            <div class="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-fade-in">
                <div class="h-1.5 bg-gradient-to-r from-primary via-primary/70 to-primary"></div>
                <div class="p-8">
                    <div class="flex items-start justify-between mb-6">
                        <div>
                            <h3 class="text-xl font-bold text-gray-800">Book your consultation</h3>
                            <p class="text-sm text-gray-500 mt-1">{{ selectedPlan.name }}</p>
                        </div>
                        <span class="text-lg font-extrabold text-gray-900">{{ formatPrice(selectedPlan.price_kobo, selectedPlan.currency) }}</span>
                    </div>

                    <div class="space-y-4">
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-1.5" for="full_name">Full name</label>
                            <input id="full_name" v-model="form.full_name" type="text" autocomplete="name"
                                   class="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                   placeholder="Jane Doe" />
                        </div>
                        <div>
                            <label class="block text-sm font-semibold text-gray-700 mb-1.5" for="email">Email address</label>
                            <input id="email" v-model="form.email" type="email" autocomplete="email"
                                   class="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                                   placeholder="jane@example.com" />
                        </div>
                    </div>

                    <p v-if="formError" class="mt-4 text-sm text-primary flex items-start gap-2">
                        <Icon name="alert-circle" class-name="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{{ formError }}</span>
                    </p>

                    <div class="mt-6 flex flex-col sm:flex-row gap-3">
                        <button @click="submitBooking" :disabled="isSubmitting"
                                class="flex-1 inline-flex items-center justify-center gap-2 bg-primary text-white font-bold py-3.5 px-6 rounded-xl shadow-lg shadow-primary/20 hover:bg-primary/90 transition duration-300 disabled:opacity-60 disabled:cursor-not-allowed">
                            <Icon name="credit-card" class-name="w-5 h-5" />
                            {{ isSubmitting ? 'Redirecting…' : 'Pay with Paystack' }}
                        </button>
                        <button @click="closeForm" :disabled="isSubmitting"
                                class="inline-flex items-center justify-center px-5 py-3.5 rounded-xl font-semibold text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition duration-300 disabled:opacity-60">
                            Cancel
                        </button>
                    </div>

                    <p class="mt-4 text-xs text-gray-400 flex items-center justify-center gap-1.5">
                        <Icon name="lock-closed" class-name="w-4 h-4" />
                        Secured by Paystack. You will pick a time after payment.
                    </p>
                </div>
            </div>
        </div>
    </main>
</template>

<style scoped>
.animate-fade-in {
    animation: fadeIn 0.5s ease-out;
}

@keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
}
</style>
